import { FC, useState, useRef, useEffect } from 'react';
import Form from 'antd/lib/form';
import Upload from 'antd/lib/upload';
import { UploadFile, UploadProps } from 'antd/lib/upload/interface';
import ReCAPTCHA from 'react-google-recaptcha';
import OSS from 'ali-oss';
import { Select } from 'antd';
import styled from './index.module.scss';
import { Option } from './Config';
import { message } from '@/components/UI/Message';
import { useLang } from '@/i18n';
import getLocal from '@/app/utils/getLocal';
import Button from '@/components/UI/Button';
import Input from '@/components/UI/Input';
import { ENGLISH_LANG, rpcMap } from '@/constants/lang';
import { euEmailSource, EU_SITES } from '@/constants/membersday';
import { createTradeIn, subscribe } from '@/service/membersday';
import { getPrivacyPolicy } from '@/utils/common';

interface IProps {
    step: number;
    handleNext: (s: number) => void;
    handleGetCode: (code: string) => void;
    title?: string;
}

// 只有eu的才有
const HOST_STS = process.env.NEXT_PUBLIC_STS_HOST_EU;
const MAX_FILESIZE = 2 * 1024 * 1024;

const TradeInForm: FC<IProps> = ({ step, handleNext, handleGetCode, title }) => {
    const { t } = useLang('membersday.return.tradein');
    const { locale } = getLocal();
    const SelectOption = Option[locale].map((item) => ({ label: item, value: item }));
    const [form] = Form.useForm();
    form.validateFields();

    const recaptchaRef = useRef(null);
    const [recaptchaResponse, setRecaptchaResponse] = useState('');
    const hl = rpcMap[ENGLISH_LANG.includes(locale) ? 'en' : locale];
    const SECRET_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_REGISTER_SECRET_KEY;

    const [form2] = Form.useForm();
    form2.validateFields();

    const [fileList, setFileList] = useState([] as UploadFile[]);

    const [form1, setForm1] = useState({
        firstName: '',
        lastName: '',
        email: '',
        countryCode: '',
        recaptchaResponse: '',
    });

    const onReCAPTCHAChange = async (captchaCode: string) => {
        if (!captchaCode) {
            return;
        }
        setRecaptchaResponse(captchaCode);
    };

    const handleBeforeUpload: UploadProps['beforeUpload'] = (file) => {
        // 最大上传文件2M
        if (file.size > MAX_FILESIZE) {
            message.error(t('tip_line2'));
            return Upload.LIST_IGNORE;
        }
        return true;
    };

    const handleUpload: UploadProps['onChange'] = ({ fileList }) => {
        setFileList(fileList);
    };

    const handleFinish = (values: any) => {
        setForm1(values);
        handleNext(step + 1);
    };

    const handleOssUp: () => Promise<string | boolean> = async () => {
        try {
            // 获取init参数
            const result1 = await fetch(`${HOST_STS}/system/oss/fileUpload/prepareSts`, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            });
            const json1 = await result1.json();
            const result2 = await fetch(`${HOST_STS}/system/oss/fileUpload/stsServer`, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            });
            const json2 = await result2.json();
            if (json1?.code === '0' && json2?.StatusCode === '200') {
                const { AccessKeyId: accessKeyId, AccessKeySecret: accessKeySecret, SecurityToken: stsToken } = json2;
                const { bucket, endpoint, region } = json1.data;

                const client = new OSS({
                    // yourRegion填写Bucket所在地域。以华东1（杭州）为例，yourRegion填写为oss-cn-hangzhou。
                    region,
                    // 从STS服务获取的临时访问密钥（AccessKey ID和AccessKey Secret）。
                    accessKeyId,
                    accessKeySecret,
                    // 从STS服务获取的安全令牌（SecurityToken）。
                    stsToken,
                    // 填写Bucket名称。
                    bucket,
                });
                // 上传文件 返回url
                const timestamp = new Date().getTime();
                const url = `/website/members-day/${locale}/${timestamp}/${fileList[0]?.name}`;
                const res = await client.put(url, fileList[0]?.originFileObj, {
                    // headers: {
                    //     'x-oss-object-acl': 'private', // 设为私有，不可直接访问
                    // },
                });
                return res.url;
            }
        } catch (err) {
            console.log(err);
            return false;
        }
    };

    const handleSubmit = async (values: any) => {
        const url = await handleOssUp();
        if (!url) return message.error('提交失败');
        const params = { ...values, ...form1, pictureUrl: url };
        createTradeIn(params, locale)
            .then(async (res) => {
                if (res.code !== '0') return message.error(res.message);
                // eu自动订阅
                if (EU_SITES.includes(locale)) {
                    try {
                        await subscribe(form1.email, locale, euEmailSource.tradeIn, {
                            sector_name: title || 'MembersDay TradeIn',
                            entry_position: 3,
                        });
                    } catch (err) {
                        console.log(err);
                    }
                }

                handleGetCode(res.data);
                handleNext(step + 1);
            })
            .catch((err) => console.log(err));
    };

    const handleBack = () => {
        setRecaptchaResponse('');
        handleNext(step - 1);
    };

    return (
        <div className={`${styled.Form} Form`}>
            {step === 1 && (
                <Form form={form} onFinish={handleFinish}>
                    <div className="row">
                        <Form.Item name="firstName" rules={[{ required: true }]}>
                            <Input placeholder={t('first_name')} maxLength={50} />
                        </Form.Item>
                        <Form.Item name="lastName">
                            <Input placeholder={t('last_name')} maxLength={50} />
                        </Form.Item>
                    </div>
                    <div className="row ">
                        <Form.Item className="item-email" name="email" rules={[{ required: true, type: 'email' }]}>
                            <Input placeholder={t('email')} maxLength={50} />
                        </Form.Item>
                        <p className="form_tips h5">{t('form_tips')}</p>
                        <Form.Item name="countryCode" rules={[{ required: true }]}>
                            <Select
                                placeholder={t('country')}
                                options={SelectOption}
                                getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                            />
                        </Form.Item>
                    </div>
                    <p className="form_tips pc">{t('form_tips')}</p>
                    <p className="form_protocol">
                        {t.rich('form_protocol', {
                            line1: (text: string) => (
                                <a className="link" href={getPrivacyPolicy(locale)} target="_blank" rel="noreferrer">
                                    {text}
                                </a>
                            ),
                            line2: (text: string) => (
                                <a className="link" href={`https://${locale}.ecoflow.com/policies/terms-of-service`} target="_blank" rel="noreferrer">
                                    {text}
                                </a>
                            ),
                        })}
                    </p>
                    <div className="tools">
                        <Form.Item shouldUpdate>
                            {() => {
                                return (
                                    <Button className="btn" htmlType="submit" disabled={!!form.getFieldsError().filter(({ errors }) => errors.length).length}>
                                        {t('btn_next')}
                                    </Button>
                                );
                            }}
                        </Form.Item>
                    </div>
                </Form>
            )}
            {step === 2 && (
                <Form form={form2} onFinish={handleSubmit}>
                    <div className="row">
                        <Form.Item name="brand" rules={[{ required: true }]}>
                            <Input placeholder={t('product')} maxLength={100} />
                        </Form.Item>
                    </div>
                    <div className="row">
                        <Form.Item name="sn" rules={[{ required: true }]}>
                            <Input placeholder={t('sn')} maxLength={50} />
                        </Form.Item>
                        <Form.Item
                            name="pictureUrl"
                            rules={[{ required: true }]}
                            extra={
                                <>
                                    <p>{t('tip_line1')}</p>
                                    <p>{t('tip_line2')}</p>
                                </>
                            }
                            style={{ position: 'relative' }}
                        >
                            <Upload
                                action=""
                                name="upload"
                                accept="image/*"
                                beforeUpload={handleBeforeUpload}
                                onChange={handleUpload}
                                maxCount={1}
                                showUploadList={false}
                                fileList={fileList}
                            >
                                <Button className="upload_btn">{fileList[0]?.name || t('upload_btn')}</Button>
                            </Upload>
                        </Form.Item>
                    </div>
                    <div className="row last">
                        <Form.Item name="upgrade" rules={[{ required: true }]}>
                            <Input placeholder={t('upgrade')} maxLength={500} />
                        </Form.Item>
                    </div>
                    <div className="row recaptcha">
                        <Form.Item name="recaptchaResponse" rules={[{ required: true }]}>
                            {/* @ts-ignore */}
                            <ReCAPTCHA theme="dark" ref={recaptchaRef} hl={hl} sitekey={SECRET_KEY} onChange={onReCAPTCHAChange} />
                        </Form.Item>
                    </div>
                    <div className="tools">
                        <Button className="btn back" onClick={handleBack}>
                            {t('btn_back')}
                        </Button>
                        <Form.Item shouldUpdate>
                            {() => (
                                <Button
                                    className="btn"
                                    htmlType="submit"
                                    disabled={!!form2.getFieldsError().filter(({ errors }) => errors.length).length || recaptchaResponse === ''}
                                >
                                    {t('btn_next')}
                                </Button>
                            )}
                        </Form.Item>
                    </div>
                </Form>
            )}
        </div>
    );
};

export default TradeInForm;
